import { Injectable } from '@angular/core';
import { FormDraftRecord } from '../form-draft';

export const FORM_DATA_EXPORT_KIND = 'ih-form-data-export';
export const FORM_DATA_EXPORT_VERSION = 1;

export interface FormDataExportFile {
  kind: typeof FORM_DATA_EXPORT_KIND;
  version: number;
  formId: string;
  formName: string;
  exportedAt: string;
  answers: Record<string, unknown>;
  foreignValues?: Record<string, unknown>;
  draft: FormDraftRecord | null;
}

@Injectable({ providedIn: 'root' })
export class FormDataTransferService {
  build(
    form: { id: string; name: string },
    answers: Record<string, unknown>,
    foreignValues: Record<string, unknown> | undefined,
    draft: FormDraftRecord | null,
  ): FormDataExportFile {
    return {
      kind: FORM_DATA_EXPORT_KIND,
      version: FORM_DATA_EXPORT_VERSION,
      formId: form.id,
      formName: form.name,
      exportedAt: new Date().toISOString(),
      answers: { ...answers },
      foreignValues: foreignValues ? { ...foreignValues } : undefined,
      draft: draft ?? null,
    };
  }

  download(file: FormDataExportFile): void {
    const blob = new Blob([JSON.stringify(file, null, 2)], {
      type: 'application/json',
    });
    const anchor = window.document.createElement('a');
    anchor.href = window.URL.createObjectURL(blob);
    const stamp = file.exportedAt.replace(/[:.]/g, '-');
    anchor.download = `${this.safeName(file.formName)}_form-data_${stamp}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(anchor.href);
  }

  parse(text: string): FormDataExportFile {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('This file is not valid JSON.');
    }
    const file = parsed as Partial<FormDataExportFile>;
    if (file?.kind !== FORM_DATA_EXPORT_KIND) {
      throw new Error('This file is not a form data export.');
    }
    if (file.version !== FORM_DATA_EXPORT_VERSION) {
      throw new Error(`Unsupported export version: ${String(file.version)}.`);
    }
    if (!file.answers || typeof file.answers !== 'object') {
      throw new Error('This export is missing its answers.');
    }
    return file as FormDataExportFile;
  }

  private safeName(name: string): string {
    return (name || 'form').replace(/[^a-z0-9-_]+/gi, '_');
  }
}
